import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Phone, Mail, MapPin, AlertTriangle, Sun, Inbox, Clock, Anchor, Target,
  TrendingUp, Wallet, CheckCircle2, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { STATUS_META, fmtEur, fmtDate, formatPeriod } from "@/admin/adminLabels";

/* ------------------- Sub-components ------------------- */
function PeriodChip({ lead }) {
  const p = formatPeriod(lead);
  if (!p.badge || p.badge === "—") return <span className="text-xs text-ink/50">—</span>;
  return (
    <span
      className="inline-flex items-center text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full"
      style={{ background: p.badgeBg, color: p.badgeColor }}
    >
      {p.badge}
    </span>
  );
}

function LeadRow({ lead, onClick }) {
  const dests = (lead.destinations || []).slice(0, 2).join(" · ") || "—";
  return (
    <button
      data-testid={`overview-lead-${lead.id}`}
      onClick={() => onClick(lead.id)}
      className="w-full text-left px-4 py-3 border-b border-[#F1F5F9] last:border-b-0 hover:bg-sand/60 transition flex items-center justify-between gap-3"
    >
      <div className="min-w-0 flex-1">
        <p className="font-medium text-navy text-sm truncate">{lead.name}</p>
        <p className="text-xs text-ink/70 truncate">
          {dests} · {fmtEur(lead.budget)} · {lead.num_participants}p
        </p>
        <div className="mt-1 flex items-center gap-2">
          <PeriodChip lead={lead} />
          <span className="text-[10px] text-ink/50">{fmtDate(lead.created_at)}</span>
        </div>
      </div>
      <ArrowRight className="h-4 w-4 text-ink/40 shrink-0" />
    </button>
  );
}

function WidgetCard({ title, icon: Icon, count, accentColor, children, empty }) {
  return (
    <div data-testid={`widget-${title.toLowerCase().replace(/[^a-z]/g, '-')}`} className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden flex flex-col">
      <div className="px-5 py-4 border-b border-[#F1F5F9] flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <span className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: accentColor + '20' }}>
            <Icon className="h-4 w-4" style={{ color: accentColor }} />
          </span>
          <p className="font-serif text-base text-navy">{title}</p>
        </div>
        <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: accentColor, color: '#FFF' }}>
          {count}
        </span>
      </div>
      <div className="flex-1 max-h-[340px] overflow-y-auto">
        {count === 0 ? (
          <p className="p-5 text-center text-xs text-ink/50">{empty || "Nessun elemento."}</p>
        ) : children}
      </div>
    </div>
  );
}

/* ------------------- Performance Section ------------------- */
function PerformanceSection({ perf }) {
  const kpis = [
    { label: "Richieste totali",    value: perf.general.total_requests, icon: Inbox },
    { label: "Richieste aperte",    value: perf.general.open_requests, icon: Clock },
    { label: "Prenotazioni chiuse", value: perf.general.closed_won, icon: CheckCircle2 },
    { label: "Conversion rate",     value: `${perf.general.conversion_rate}%`, icon: TrendingUp },
  ];
  // Solo 3 KPI economici come richiesto: Fatturato, Fatturato potenziale, Commissione media (+ inciso valore medio richieste)
  return (
    <div className="space-y-6">
      <section>
        <p className="label-eyebrow">Performance</p>
        <div className="mt-3 grid grid-cols-2 lg:grid-cols-4 gap-3">
          {kpis.map((k, i) => (
            <div key={i} data-testid={`perf-kpi-${i}`} className="bg-white border border-[#E2E8F0] rounded-xl p-5">
              <div className="flex items-center gap-2">
                <k.icon className="h-4 w-4 text-turquoise" />
                <span className="text-[11px] uppercase tracking-wider text-ink/70">{k.label}</span>
              </div>
              <p className="mt-3 font-serif text-3xl text-navy">{k.value}</p>
            </div>
          ))}
        </div>
      </section>

      <section>
        <p className="label-eyebrow">Economica</p>
        <p className="mt-1 text-xs text-ink/60">Commissione Seamatch calcolata automaticamente all'8% del valore pratica.</p>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div data-testid="econ-realized" className="bg-white border border-[#E2E8F0] rounded-xl p-5">
            <p className="text-[11px] uppercase tracking-wider text-ink/70">Fatturato</p>
            <p className="mt-3 font-serif text-3xl text-navy">{fmtEur(perf.economic.revenue_realized)}</p>
            <p className="mt-1 text-[10px] text-ink/50">Commissioni da pratiche confermate</p>
          </div>
          <div data-testid="econ-potential" className="bg-white border border-[#E2E8F0] rounded-xl p-5">
            <p className="text-[11px] uppercase tracking-wider text-ink/70">Fatturato potenziale</p>
            <p className="mt-3 font-serif text-3xl text-navy">{fmtEur(perf.economic.revenue_potential)}</p>
            <p className="mt-1 text-[10px] text-ink/50">8% del valore pratica per pipeline aperta</p>
          </div>
          <div data-testid="econ-avg-commission" className="bg-white border border-[#E2E8F0] rounded-xl p-5">
            <p className="text-[11px] uppercase tracking-wider text-ink/70">Commissione media</p>
            <p className="mt-3 font-serif text-3xl text-navy">{fmtEur(perf.economic.avg_commission)}</p>
            <p className="mt-1 text-[11px] text-ink/60">
              Valore medio richieste: <strong className="text-navy">{fmtEur(perf.economic.avg_request_value)}</strong>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ------------------- Goal Tracker ------------------- */
function GoalTracker() {
  const [goal, setGoal] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ target_date: "", target_amount_eur: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/goal");
      setGoal(r.data);
      if (r.data) {
        setForm({
          target_date: (r.data.target_date || "").slice(0, 10),
          target_amount_eur: r.data.target_amount_eur || "",
        });
      }
    } catch (e) { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.target_date || !form.target_amount_eur) {
      toast.error("Inserisci data e obiettivo €");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/admin/goal", {
        target_date: form.target_date,
        target_amount_eur: parseFloat(form.target_amount_eur),
      });
      setGoal(r.data);
      setEditing(false);
      toast.success("Obiettivo aggiornato");
    } catch (e) {
      toast.error("Errore salvataggio");
    } finally { setSaving(false); }
  };

  const progressPct = goal && goal.target_amount_eur
    ? Math.min(100, Math.round((goal.achieved / goal.target_amount_eur) * 100))
    : 0;

  return (
    <section className="bg-gradient-to-br from-navy to-[#06101f] text-white rounded-2xl p-6 lg:p-8" data-testid="goal-tracker">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="h-10 w-10 rounded-lg bg-gold/20 flex items-center justify-center">
            <Target className="h-5 w-5 text-gold" />
          </span>
          <div>
            <p className="text-[11px] uppercase tracking-wider text-gold font-semibold">Obiettivo economico</p>
            <p className="font-serif text-2xl">{goal ? `${fmtEur(goal.target_amount_eur)} entro ${new Date(goal.target_date).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}` : "Nessun obiettivo impostato"}</p>
          </div>
        </div>
        <Button data-testid="goal-edit-btn" variant="outline" onClick={() => setEditing(!editing)}
                className="border-white/40 text-white hover:bg-white/10 hover:text-white">
          {editing ? "Annulla" : (goal ? "Modifica" : "Imposta")}
        </Button>
      </div>

      {editing && (
        <div className="mt-5 grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-gold">Data obiettivo</Label>
            <Input data-testid="goal-date" type="date" value={form.target_date}
                   onChange={(e) => setForm({ ...form, target_date: e.target.value })}
                   className="mt-1 h-10 bg-white/10 border-white/20 text-white" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-gold">Obiettivo € (commissioni)</Label>
            <Input data-testid="goal-amount" type="number" value={form.target_amount_eur}
                   onChange={(e) => setForm({ ...form, target_amount_eur: e.target.value })}
                   placeholder="50000" className="mt-1 h-10 bg-white/10 border-white/20 text-white" />
          </div>
          <Button data-testid="goal-save" onClick={save} disabled={saving}
                  className="sm:col-span-2 bg-gold text-navy hover:bg-[#b48e57] h-11">
            {saving ? "Salvataggio…" : "Salva obiettivo"}
          </Button>
        </div>
      )}

      {goal && !editing && (
        <>
          <div className="mt-6">
            <div className="flex items-center justify-between text-xs uppercase tracking-wider text-white/70 mb-2">
              <span>Avanzamento</span>
              <span className="font-semibold text-gold">{progressPct}%</span>
            </div>
            <div className="h-2 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-gold transition-all" style={{ width: `${progressPct}%` }} />
            </div>
            <div className="mt-2 flex items-center justify-between text-xs text-white/60">
              <span>{fmtEur(goal.achieved)} realizzati</span>
              <span>{fmtEur(goal.remaining)} mancanti</span>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { l: "Giorni residui", v: goal.days_left },
              { l: "Valore medio pratica", v: fmtEur(goal.avg_booking_value || goal.avg_request_value), hint: "Media reale dalle pratiche chiuse" },
              { l: "Commissione media (8%)", v: fmtEur(goal.avg_commission) },
              { l: "Pratiche da chiudere", v: goal.bookings_needed },
              { l: "Ritmo settimanale", v: `${goal.pace_per_week}/sett` },
              { l: "Probabilità raggiungimento", v: `${goal.achievement_probability_pct}%`, hint: "basata sulla pipeline aperta" },
            ].map((k, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4">
                <p className="text-[10px] uppercase tracking-wider text-white/60">{k.l}</p>
                <p className="mt-2 font-serif text-xl text-white">{k.v}</p>
                {k.hint && <p className="mt-1 text-[10px] text-white/40">{k.hint}</p>}
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

/* ------------------- MAIN ------------------- */
export default function OverviewOperative({ onSelectLead }) {
  const [overview, setOverview] = useState(null);
  const [perf, setPerf] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/admin/overview").then(r => setOverview(r.data)).catch(() => {}),
      api.get("/admin/performance").then(r => setPerf(r.data)).catch(() => {}),
    ]);
  }, []);

  if (!overview || !perf) {
    return <div className="text-ink">Caricamento panoramica…</div>;
  }

  return (
    <div className="space-y-8">
      <div>
        <p className="label-eyebrow">Operativo · Oggi</p>
        <h2 className="mt-2 font-serif text-2xl text-navy">Cosa fare oggi</h2>
        <p className="mt-1 text-sm text-ink/70">Le richieste e i messaggi che richiedono la tua attenzione.</p>
      </div>

      {/* 4 widget grid - operativi */}
      <div className="grid md:grid-cols-2 xl:grid-cols-2 gap-4">
        <WidgetCard
          title="Nuove oggi"
          icon={Sun}
          count={overview.counts.nuove_oggi}
          accentColor="#0EA5E9"
          empty="Nessuna nuova richiesta arrivata oggi."
        >
          {overview.nuove_oggi.map(l => <LeadRow key={l.id} lead={l} onClick={onSelectLead} />)}
        </WidgetCard>

        <WidgetCard
          title="Richieste urgenti"
          icon={AlertTriangle}
          count={overview.counts.richieste_urgenti}
          accentColor="#DC2626"
          empty="Tutte le richieste recenti sono state riscontrate ✓"
        >
          {overview.richieste_urgenti.map(l => <LeadRow key={l.id} lead={l} onClick={onSelectLead} />)}
        </WidgetCard>

        <WidgetCard
          title="In attesa di preventivo"
          icon={Clock}
          count={overview.counts.in_attesa_preventivo}
          accentColor="#F59E0B"
          empty="Imposta manualmente una richiesta in stato 'In ricerca preventivi' per vederla qui."
        >
          {overview.in_attesa_preventivo.map(l => <LeadRow key={l.id} lead={l} onClick={onSelectLead} />)}
        </WidgetCard>

        <WidgetCard
          title="Charter da contattare"
          icon={Anchor}
          count={overview.counts.charter_da_contattare}
          accentColor="#0B192C"
          empty="Nessuna destinazione con richieste aperte."
        >
          {overview.charter_da_contattare.map((c, i) => (
            <div key={i} className="px-4 py-3 border-b border-[#F1F5F9] last:border-b-0">
              <p className="font-medium text-navy text-sm">{c.label}</p>
              <p className="text-xs text-ink/70 mt-0.5 flex items-center gap-1.5">
                <MapPin className="h-3 w-3" /> {c.destination} · {c.requests_count} richieste aperte
              </p>
            </div>
          ))}
        </WidgetCard>
      </div>

      {/* Performance + Economic */}
      <PerformanceSection perf={perf} />

      {/* Goal tracker */}
      <GoalTracker />
    </div>
  );
}
